import React, { useEffect, useState } from "react";
import Layout, { PageHeader } from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";
import { PlugsConnected, Envelope, PaperPlaneTilt, CheckCircle, XCircle, Plug, CopySimple, ArrowsClockwise } from "@phosphor-icons/react";

const DEFAULTS = {
  imap_host: "", imap_port: 993, imap_user: "", imap_password: "",
  imap_use_ssl: true, imap_mailbox: "INBOX", imap_enabled: false,
  smtp_host: "mail.smtp2go.com", smtp_port: 587, smtp_user: "", smtp_password: "",
  smtp_from_email: "", smtp_enabled: false,
};

export default function Integrations() {
  const [form, setForm] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [testResult, setTestResult] = useState({});
  const [inboundToken, setInboundToken] = useState("");
  const [rotating, setRotating] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/integrations"),
        api.get("/inbound-webhook/token").catch(() => ({ data: { token: "" } })),
      ]);
      setForm({ ...DEFAULTS, ...a.data });
      setInboundToken(b.data?.token || "");
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const rotateInbound = async () => {
    if (!window.confirm("Rotate inbound webhook token? Existing senders will need the new URL.")) return;
    setRotating(true);
    try {
      const { data } = await api.post("/inbound-webhook/rotate");
      setInboundToken(data.token);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setRotating(false); }
  };

  const inboundUrl = inboundToken
    ? `${process.env.REACT_APP_BACKEND_URL}/api/inbound-webhook/${inboundToken}`
    : "";

  const copyInbound = () => {
    if (!inboundUrl) return;
    navigator.clipboard.writeText(inboundUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const save = async () => {
    setSaving(true); setError(""); setSuccess("");
    try {
      const payload = {
        ...form,
        imap_port: Number(form.imap_port) || 993,
        smtp_port: Number(form.smtp_port) || 587,
      };
      const { data } = await api.put("/integrations", payload);
      setForm({ ...DEFAULTS, ...data });
      setSuccess("Saved.");
      setTimeout(() => setSuccess(""), 3000);
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const testImap = async () => {
    setTestResult({ ...testResult, imap: { loading: true } });
    try {
      const { data } = await api.post("/integrations/test-imap");
      setTestResult({ ...testResult, imap: data });
    } catch (e) {
      setTestResult({ ...testResult, imap: { ok: false, message: formatApiError(e.response?.data?.detail) || e.message } });
    }
  };
  const testSmtp = async () => {
    setTestResult({ ...testResult, smtp: { loading: true } });
    try {
      const { data } = await api.post("/integrations/test-smtp");
      setTestResult({ ...testResult, smtp: data });
    } catch (e) {
      setTestResult({ ...testResult, smtp: { ok: false, message: formatApiError(e.response?.data?.detail) || e.message } });
    }
  };
  const sendTest = async () => {
    setTestResult({ ...testResult, send: { loading: true } });
    try {
      const { data } = await api.post("/integrations/send-test-email", {});
      setTestResult({ ...testResult, send: data });
    } catch (e) {
      setTestResult({ ...testResult, send: { ok: false, message: formatApiError(e.response?.data?.detail) || e.message } });
    }
  };

  return (
    <Layout>
      <PageHeader title="Integrations" subtitle="IMAP inbound + SMTP (SMTP2GO) outbound for backup emails" />
      <div className="p-6 space-y-6 max-w-4xl">
        {loading ? <div className="text-sm text-slate-500">loading…</div> : (
          <>
            <section className="sw-card" data-testid="integrations-imap">
              <header className="px-5 py-3 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Envelope weight="duotone" size={18} className="text-brand-cyan" />
                  <h2 className="font-display font-bold tracking-tight">Inbound — IMAP polling</h2>
                </div>
                <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">
                  <input type="checkbox" checked={form.imap_enabled} onChange={(e) => setForm({ ...form, imap_enabled: e.target.checked })} data-testid="imap-enabled-checkbox" />
                  enabled
                </label>
              </header>
              <div className="p-5 grid sm:grid-cols-2 gap-4">
                <FieldI label="Host"><input value={form.imap_host || ""} onChange={(e) => setForm({ ...form, imap_host: e.target.value })} data-testid="imap-host-input" placeholder="imap.example.com" /></FieldI>
                <FieldI label="Port"><input type="number" value={form.imap_port} onChange={(e) => setForm({ ...form, imap_port: e.target.value })} data-testid="imap-port-input" /></FieldI>
                <FieldI label="Username"><input value={form.imap_user || ""} onChange={(e) => setForm({ ...form, imap_user: e.target.value })} data-testid="imap-user-input" /></FieldI>
                <FieldI label="Password"><input type="password" value={form.imap_password || ""} onChange={(e) => setForm({ ...form, imap_password: e.target.value })} data-testid="imap-password-input" /></FieldI>
                <FieldI label="Mailbox"><input value={form.imap_mailbox || "INBOX"} onChange={(e) => setForm({ ...form, imap_mailbox: e.target.value })} data-testid="imap-mailbox-input" /></FieldI>
                <FieldI label="Use SSL">
                  <label className="flex items-center h-9 gap-2 text-sm text-slate-200">
                    <input type="checkbox" checked={form.imap_use_ssl} onChange={(e) => setForm({ ...form, imap_use_ssl: e.target.checked })} data-testid="imap-ssl-checkbox" />
                    SSL/TLS
                  </label>
                </FieldI>
              </div>
              <div className="px-5 py-3 border-t border-border flex items-center justify-between bg-background-deep/30">
                <ResultBadge res={testResult.imap} label="IMAP" />
                <button onClick={testImap} data-testid="imap-test-btn"
                  className="px-3 py-1.5 text-sm border border-border hover:bg-white/5 rounded-md">Test connection</button>
              </div>
            </section>

            <section className="sw-card" data-testid="integrations-smtp">
              <header className="px-5 py-3 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <PaperPlaneTilt weight="duotone" size={18} className="text-brand-cyan" />
                  <h2 className="font-display font-bold tracking-tight">Outbound — SMTP2GO</h2>
                </div>
                <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">
                  <input type="checkbox" checked={form.smtp_enabled} onChange={(e) => setForm({ ...form, smtp_enabled: e.target.checked })} data-testid="smtp-enabled-checkbox" />
                  enabled
                </label>
              </header>
              <div className="px-5 pt-4 -mb-2 text-[11px] text-slate-400 bg-brand-cyan/5 border-l-2 border-brand-cyan/40 ml-5 mr-5 rounded-r px-3 py-2">
                Get a username + password from{" "}
                <a href="https://app.smtp2go.com/settings/users/smtpusers/" target="_blank" rel="noopener noreferrer"
                  className="text-brand-cyan hover:underline" data-testid="smtp2go-users-link">
                  app.smtp2go.com → SMTP Users
                </a>
                . Verify your sender domain at{" "}
                <a href="https://app.smtp2go.com/settings/senders/domains/" target="_blank" rel="noopener noreferrer"
                  className="text-brand-cyan hover:underline" data-testid="smtp2go-domains-link">
                  Senders → Domains
                </a>.
                Free tier: 1,000 emails/month.
              </div>
              <div className="p-5 grid sm:grid-cols-2 gap-4">
                <FieldI label="Host"><input value={form.smtp_host || ""} onChange={(e) => setForm({ ...form, smtp_host: e.target.value })} data-testid="smtp-host-input" /></FieldI>
                <FieldI label="Port (587 STARTTLS · 2525 alt · 465 SSL)"><input type="number" value={form.smtp_port} onChange={(e) => setForm({ ...form, smtp_port: e.target.value })} data-testid="smtp-port-input" /></FieldI>
                <FieldI label="SMTP2GO Username"><input value={form.smtp_user || ""} onChange={(e) => setForm({ ...form, smtp_user: e.target.value })} data-testid="smtp-user-input" placeholder="your-smtp2go-username" /></FieldI>
                <FieldI label="SMTP2GO Password"><input type="password" value={form.smtp_password || ""} onChange={(e) => setForm({ ...form, smtp_password: e.target.value })} data-testid="smtp-password-input" /></FieldI>
                <FieldI label="From email (must be a verified sender)" wide><input type="email" value={form.smtp_from_email || ""} onChange={(e) => setForm({ ...form, smtp_from_email: e.target.value })} data-testid="smtp-from-input" placeholder="alerts@sentryit.com" /></FieldI>
              </div>
              <div className="px-5 py-3 border-t border-border flex items-center justify-between bg-background-deep/30">
                <ResultBadge res={testResult.smtp} label="SMTP" />
                <div className="flex gap-2">
                  <button onClick={testSmtp} data-testid="smtp-test-btn"
                    className="px-3 py-1.5 text-sm border border-border hover:bg-white/5 rounded-md">Test connection</button>
                  <button onClick={sendTest} data-testid="smtp-send-test-btn"
                    className="px-3 py-1.5 text-sm bg-brand-cyan text-brand-navy hover:bg-brand-cyan/90 rounded-md font-semibold">Send test email</button>
                </div>
              </div>
              {testResult.send && (
                <div className="px-5 py-3 border-t border-border">
                  <ResultBadge res={testResult.send} label="Send test" />
                </div>
              )}
            </section>

            <section className="sw-card" data-testid="integrations-inbound-webhook">
              <header className="px-5 py-3 border-b border-border flex items-center gap-2">
                <Plug weight="duotone" size={18} className="text-brand-cyan" />
                <h2 className="font-display font-bold tracking-tight">Inbound webhook — SendGrid / Mailgun / Cloudflare</h2>
                <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-emerald-400 ml-auto">live</span>
              </header>
              <div className="p-5 space-y-3">
                <p className="text-xs text-slate-400">
                  Forward backup-result emails (or any JSON POST) to the URL below. SentryWatch will parse + classify them automatically.
                  Compatible with SendGrid Inbound Parse, Mailgun Routes, Cloudflare Email Workers, and generic webhooks.
                </p>
                <div className="flex items-center gap-2 bg-background-deep border border-border rounded p-2">
                  <code className="text-xs text-brand-cyan font-mono flex-1 break-all" data-testid="inbound-webhook-url">
                    {inboundUrl || "—"}
                  </code>
                  <button onClick={copyInbound} disabled={!inboundUrl}
                    data-testid="inbound-copy-btn"
                    className="px-2 py-1 text-xs border border-border hover:bg-white/5 rounded inline-flex items-center gap-1 disabled:opacity-50">
                    <CopySimple size={12} weight="bold" /> {copied ? "Copied!" : "Copy"}
                  </button>
                  <button onClick={rotateInbound} disabled={rotating}
                    data-testid="inbound-rotate-btn"
                    title="Rotate token (revokes the current URL)"
                    className="px-2 py-1 text-xs border border-border hover:bg-white/5 rounded inline-flex items-center gap-1">
                    <ArrowsClockwise size={12} weight="bold" /> {rotating ? "…" : "Rotate"}
                  </button>
                </div>
                <div className="text-[11px] text-slate-500">
                  Expected JSON body: <span className="text-slate-300">{"{ subject, sender|from, body_plain|text, body_html?, client_id? }"}</span>
                </div>
              </div>
            </section>

            <section className="sw-card">
              <header className="px-5 py-3 border-b border-border flex items-center gap-2">
                <PlugsConnected weight="duotone" size={18} className="text-brand-cyan" />
                <h2 className="font-display font-bold tracking-tight">Vendor API integrations</h2>
                <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-amber-400 ml-auto">coming soon</span>
              </header>
              <div className="p-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {["Veeam", "Acronis", "Datto", "N-able"].map((v) => (
                  <div key={v} className="p-4 border border-border rounded text-center bg-background-deep/40">
                    <div className="text-sm text-slate-100 font-display font-semibold">{v}</div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400 mt-1">api ready</div>
                  </div>
                ))}
              </div>
            </section>

            {error && <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded px-3 py-2" data-testid="integrations-error">{error}</div>}
            {success && <div className="text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded px-3 py-2" data-testid="integrations-success">{success}</div>}

            <div className="flex justify-end">
              <button onClick={save} disabled={saving} data-testid="integrations-save-btn"
                className="px-5 py-2 text-sm bg-brand-cyan text-brand-navy hover:bg-brand-cyan/90 disabled:opacity-60 rounded-md font-semibold">
                {saving ? "Saving…" : "Save integrations"}
              </button>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}

function FieldI({ label, children, wide }) {
  return (
    <div className={wide ? "sm:col-span-2" : ""}>
      <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400 mb-1.5">{label}</label>
      {React.cloneElement(children, {
        className: "w-full bg-background-deep border border-border rounded px-3 py-2 text-sm focus:outline-none focus:border-brand-cyan transition-colors",
      })}
    </div>
  );
}

function ResultBadge({ res, label }) {
  if (!res) return <span className="text-xs text-slate-500">{label}: not tested</span>;
  if (res.loading) return <span className="text-xs text-slate-300">{label}: testing…</span>;
  if (res.ok) return <span className="text-xs text-emerald-400 inline-flex items-center gap-1"><CheckCircle size={14} weight="fill" /> {label}: {res.message}</span>;
  return <span className="text-xs text-red-400 inline-flex items-center gap-1"><XCircle size={14} weight="fill" /> {label}: {res.message}</span>;
}
